/* sielle-behavior.js
 * Shared vanilla-JS behavior for the Sielle landing clone sections.
 * Ported 1:1 (algorithms preserved) from the original Claude Design
 * component logic (dc-runtime "Component" class) to plain DOM code so it
 * runs on any Shopify theme without a framework.
 *
 * Covers: add-to-cart wiring for every [data-clone-cta], the rotating
 * marquee, animated [data-count] counters, sticky mobile buy-bar reveal,
 * drag + infinite-wrap carousels ([data-sielle-track]), FAQ-independent
 * generic accordion helper (sections wire their own, this is a fallback),
 * the Amazon-style reviews block (generation, filcountingter, sort,
 * pagination, lightbox).
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- */
  /* Add to cart — every [data-clone-cta] posts to /cart/add.js        */
  /* ---------------------------------------------------------------- */
  function wireAddToCart() {
    document.querySelectorAll('[data-clone-cta]').forEach(function (btn) {
      if (btn.__sielleWired) return;
      btn.__sielleWired = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var id = btn.getAttribute('data-product-id');
        if (!id) { window.location.href = '/cart'; return; }
        var qtyInput = document.querySelector('[data-sielle-qty]');
        var quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
        var buyNow = btn.hasAttribute('data-buy-now');
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: quantity }] })
        }).then(function (res) {
          if (!res.ok) throw new Error('add-to-cart failed');
          return res.json();
        }).then(function () {
          if (buyNow) { window.location.href = '/checkout'; return; }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          var drawer = document.querySelector('cart-drawer, .cart-drawer, [data-cart-drawer]');
          if (drawer && typeof drawer.open === 'function') {
            drawer.open();
          } else if (drawer) {
            drawer.classList.add('is-open', 'active', 'open');
            document.dispatchEvent(new CustomEvent('cart:open'));
            setTimeout(function () {
              var stillClosed = !document.body.classList.contains('cart-drawer-open') &&
                !document.querySelector('.cart-drawer.is-open, .cart-drawer.active, .cart-drawer.open');
              if (stillClosed) window.location.href = '/cart';
            }, 500);
          } else {
            window.location.href = '/cart';
          }
        }).catch(function () {
          window.location.href = '/cart';
        });
      });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Marquee ticker — rotate messages every 3.2s with a fade           */
  /* ---------------------------------------------------------------- */
  function initMarquee() {
    var el = document.querySelector('[data-sielle-marquee]');
    if (!el || el.__init) return;
    el.__init = true;
    var raw = el.getAttribute('data-messages') || '';
    var messages = raw.split('|').map(function (s) { return s.trim(); }).filter(Boolean);
    if (messages.length < 2) return;
    var idx = 0;
    setInterval(function () {
      el.style.opacity = '0';
      setTimeout(function () {
        idx = (idx + 1) % messages.length;
        el.textContent = messages[idx];
        el.style.opacity = '1';
      }, 350);
    }, 3200);
  }

  /* ---------------------------------------------------------------- */
  /* Animated [data-count] counters, run once in view                  */
  /* ---------------------------------------------------------------- */
  function initCounters() {
    var counters = document.querySelectorAll('[data-count]:not([data-count-init])');
    var runCounter = function (node) {
      node.setAttribute('data-count-init', '1');
      var target = parseInt(node.getAttribute('data-count'), 10) || 0;
      var dur = 1600;
      var start = performance.now();
      var fmt = function (n) { return n.toLocaleString('es-CO'); };
      var tick = function (now) {
        var p = Math.min(1, (now - start) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        node.textContent = fmt(Math.round(target * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { runCounter(en.target); io.unobserve(en.target); }
        });
      }, { threshold: 0, rootMargin: '0px 0px -5% 0px' });
      counters.forEach(function (c) {
        var r = c.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) runCounter(c);
        else io.observe(c);
      });
    } else {
      counters.forEach(runCounter);
    }
  }

  /* ---------------------------------------------------------------- */
  /* Sticky mobile buy-bar reveal after hero                           */
  /* ---------------------------------------------------------------- */
  function initStickyBar() {
    var bar = document.querySelector('[data-sielle-sticky]');
    if (!bar || bar.__init) return;
    bar.__init = true;
    var onScroll = function () {
      var show = window.scrollY > window.innerHeight * 0.85;
      bar.classList.toggle('is-visible', show);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------------------------------------------------------------- */
  /* Generic drag-to-scroll + infinite wrap for [data-sielle-track]    */
  /* ---------------------------------------------------------------- */
  function setupDrag(el) {
    if (!el || el.__dragInit) return;
    el.__dragInit = true;
    var isDown = false, startX = 0, startScroll = 0, moved = false;
    el.style.cursor = 'grab';
    var release = function () {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = 'grab';
      el.style.scrollSnapType = 'x mandatory';
    };
    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      isDown = true; moved = false; startX = e.clientX; startScroll = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.scrollSnapType = 'none';
    }, true);
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    window.addEventListener('blur', release);
    window.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      if (e.buttons === 0) { release(); return; }
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      el.scrollLeft = startScroll - dx;
    });
    el.addEventListener('click', function (e) { if (moved) e.preventDefault(); }, true);
  }

  function setupInfiniteWrap(el) {
    var atEnd = false, atStart = false;
    var updateFlags = function () {
      var max = el.scrollWidth - el.clientWidth;
      atEnd = max > 2 && el.scrollLeft >= max - 2;
      atStart = el.scrollLeft <= 2;
    };
    el.addEventListener('scroll', updateFlags, { passive: true });
    updateFlags();
    el.addEventListener('wheel', function (e) {
      var max = el.scrollWidth - el.clientWidth;
      if (max <= 2) return;
      if (atEnd && e.deltaX > 0) { el.scrollTo({ left: 0, behavior: 'smooth' }); atEnd = false; }
      else if (atStart && e.deltaX < 0) { el.scrollTo({ left: max, behavior: 'smooth' }); atStart = false; }
    }, { passive: true });
    var touchStartX = null;
    el.addEventListener('touchstart', function (e) { touchStartX = e.touches[0].clientX; }, { passive: true });
    el.addEventListener('touchend', function (e) {
      if (touchStartX == null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      var max = el.scrollWidth - el.clientWidth;
      touchStartX = null;
      if (max <= 2) return;
      if (atEnd && dx < -30) { el.scrollTo({ left: 0, behavior: 'smooth' }); atEnd = false; }
      else if (atStart && dx > 30) { el.scrollTo({ left: max, behavior: 'smooth' }); atStart = false; }
    }, { passive: true });
  }

  function updateTrackDots(track) {
    var dotsWrap = document.querySelector('[data-track-dots-for="' + track.id + '"]');
    if (!dotsWrap) return;
    var items = track.querySelectorAll(':scope > *');
    if (!items.length) return;
    var center = track.scrollLeft + track.clientWidth / 2;
    var closest = 0, dist = Infinity;
    items.forEach(function (it, i) {
      var c = it.offsetLeft + it.clientWidth / 2;
      if (Math.abs(c - center) < dist) { dist = Math.abs(c - center); closest = i; }
    });
    dotsWrap.querySelectorAll('[data-dot]').forEach(function (d) {
      var active = (parseInt(d.getAttribute('data-dot'), 10) || 0) === closest;
      d.style.opacity = active ? '1' : '.3';
    });
  }

  function initTracks() {
    document.querySelectorAll('[data-sielle-track]').forEach(function (track) {
      if (track.__trackInit) return;
      track.__trackInit = true;
      setupDrag(track);
      setupInfiniteWrap(track);
      var raf = null;
      track.addEventListener('scroll', function () {
        if (raf) clearTimeout(raf);
        raf = setTimeout(function () { updateTrackDots(track); }, 80);
      }, { passive: true });
      updateTrackDots(track);

      var prev = document.querySelector('[data-track-prev="' + track.id + '"]');
      var next = document.querySelector('[data-track-next="' + track.id + '"]');
      var step = function () {
        var first = track.querySelector(':scope > *');
        return first ? first.getBoundingClientRect().width + 20 : 300;
      };
      if (prev) prev.addEventListener('click', function () {
        var max = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft <= 4) track.scrollTo({ left: max, behavior: 'smooth' });
        else track.scrollBy({ left: -step(), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        var max = track.scrollWidth - track.clientWidth;
        if (track.scrollLeft >= max - 4) track.scrollTo({ left: 0, behavior: 'smooth' });
        else track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Hero gallery — scroll-snap track + thumbs + dots + prev/next      */
  /* ---------------------------------------------------------------- */
  function initHeroGallery() {
    var track = document.querySelector('[data-hero-track]');
    if (!track || track.__init) return;
    track.__init = true;
    setupDrag(track);
    setupInfiniteWrap(track);

    var updateActive = function () {
      var w = track.clientWidth || 1;
      var idx = Math.round(track.scrollLeft / w);
      document.querySelectorAll('[data-hero-dot]').forEach(function (d) {
        var active = (parseInt(d.getAttribute('data-hero-dot'), 10) || 0) === idx;
        d.style.opacity = active ? '1' : '.28';
        d.style.transform = active ? 'scale(1.25)' : 'scale(1)';
      });
      document.querySelectorAll('[data-hero-thumb]').forEach(function (d) {
        var active = (parseInt(d.getAttribute('data-hero-thumb'), 10) || 0) === idx;
        d.style.borderColor = active ? 'var(--sielle-blue)' : 'transparent';
        d.style.opacity = active ? '1' : '.6';
      });
    };
    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateActive);
    }, { passive: true });
    updateActive();

    document.querySelectorAll('[data-hero-dot], [data-hero-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var i = parseInt(btn.getAttribute('data-hero-dot') || btn.getAttribute('data-hero-thumb'), 10) || 0;
        track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
      });
    });
    var prev = document.querySelector('[data-hero-prev]');
    var next = document.querySelector('[data-hero-next]');
    if (prev) prev.addEventListener('click', function () {
      var w = track.clientWidth, max = track.scrollWidth - w;
      if (track.scrollLeft <= 4) track.scrollTo({ left: max, behavior: 'smooth' });
      else track.scrollBy({ left: -w, behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function () {
      var w = track.clientWidth, max = track.scrollWidth - w;
      if (track.scrollLeft >= max - 4) track.scrollTo({ left: 0, behavior: 'smooth' });
      else track.scrollBy({ left: w, behavior: 'smooth' });
    });
  }

  /* ---------------------------------------------------------------- */
  /* Amazon-style reviews block — generation, filter, sort, pagination */
  /* Ported 1:1 from the source's generateAllReviews()/renderVals().   */
  /* ---------------------------------------------------------------- */
  function generateAllReviews() {
    var remaining = { 5: 290, 4: 40, 3: 10, 2: 4, 1: 2 };
    var order = [5, 4, 3, 2, 1];
    var ratings = [];
    while (ratings.length < 346) {
      for (var oi = 0; oi < order.length; oi++) {
        var r = order[oi];
        if (remaining[r] > 0) { ratings.push(r); remaining[r]--; }
      }
    }
    var frag = {
      5: {
        open: ["Excelente producto,", "Me encantó desde el primer uso,", "Superó mis expectativas,", "No puedo creer lo bien que funciona,", "Desde que lo compré,", "Se los recomiendo a ojos cerrados,", "Cambió mi rutina de las noches,", "Justo lo que necesitaba,"],
        body: ["el masaje llega profundo al cuello y los hombros", "el calor se siente real y ayuda a soltar la tensión", "las correas se ajustan fácil a la espalda baja", "lo uso después del trabajo y quedo mucho más relajada", "no hace ruido y se siente como manos expertas", "es liviano y fácil de llevar a cualquier parte", "la batería dura varias sesiones sin problema", "ayuda con el dolor que me daba de estar sentada todo el día", "se siente como un masaje de spa pero en casa", "cumple exactamente lo que promete la descripción"],
        close: ["totalmente recomendado.", "ya es parte de mi rutina diaria.", "lo volvería a comprar sin pensarlo.", "una compra que valió la pena.", "mi cuerpo me lo agradece cada día.", "no me arrepiento nada.", "5 estrellas bien merecidas.", "es el mejor regalo que me he dado.", "ahora no puedo vivir sin él."]
      },
      4: {
        open: ["Buen producto,", "Cumple bastante bien,", "Estoy conforme,", "En general funciona bien,", "Me gustó bastante,", "Vale la pena aunque"],
        body: ["el masaje es bueno aunque esperaba un poco más de intensidad", "se calienta rápido pero pesa un poco más de lo que pensé", "funciona bien en cuello y hombros, en la espalda baja cuesta acomodarlo", "la batería dura menos de lo que esperaba", "tardó unos días más de lo previsto en llegar", "el cable podría ser un poco más largo", "faltó un modo más de intensidad"],
        close: ["aun así lo recomiendo.", "cumple lo que promete.", "buena relación precio-calidad.", "seguiré usándolo de todas formas.", "no está mal para el precio."]
      },
      3: {
        open: ["Está bien,", "Cumple,", "Es correcto,", "En términos generales,"],
        body: ["pero esperaba un masaje más intenso", "aunque no es tan potente como pensé por el video", "sirve para relajar un poco, nada extraordinario", "las correas se sueltan con el uso"],
        close: ["ni más ni menos.", "cumple lo básico.", "podría mejorar."]
      },
      2: {
        open: ["No cumplió del todo,", "Esperaba más,", "Regular,"],
        body: ["el calor tarda en sentirse y la intensidad se queda corta", "no es tan fuerte como lo esperaba para espalda baja", "la calidad de las correas deja que desear"],
        close: ["no lo compraría de nuevo.", "me quedé con dudas."]
      },
      1: {
        open: ["No funcionó,", "Mala experiencia,"],
        body: ["dejó de calentar a la semana de uso", "no sentí el alivio que ofrecen en la publicidad"],
        close: ["no lo recomiendo.", "pedí que me lo cambiaran."]
      }
    };
    var names = ["Valentina R.", "Marcela T.", "Andrés G.", "Laura M.", "Carolina P.", "Juliana S.", "Camilo H.", "Natalia V.", "Diego F.", "Paola S.", "Ricardo L.", "Daniela C.", "Felipe A.", "Sandra M.", "Jorge P.", "Alejandra N.", "Mauricio B.", "Catalina R.", "Esteban Q.", "Liliana O.", "Sebastián V.", "Ximena T.", "Iván D.", "Gabriela E.", "Nicolás F."];
    var cities = ["Bogotá", "Medellín", "Cali", "Barranquilla", "Bucaramanga", "Pereira", "Cartagena", "Manizales", "Cúcuta", "Santa Marta"];
    var sloppify = function (s) {
      var t = s.toLowerCase().replace(/á/g, 'a').replace(/é/g, 'e').replace(/í/g, 'i').replace(/ó/g, 'o').replace(/ú/g, 'u');
      t = t.replace(/, /g, ' ').replace(/\.$/, '');
      return t;
    };
    var counters = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    return ratings.map(function (rating, i) {
      var hasText = rating <= 3 ? true : (i % 2 === 0);
      var text = null;
      if (hasText) {
        var f = frag[rating];
        var n = counters[rating]++;
        text = f.open[n % f.open.length] + ' ' + f.body[(n + 3) % f.body.length] + ' ' + f.close[(n + 5) % f.close.length];
        if (i % 6 === 0) text = sloppify(text);
      }
      var hasImage = hasText && (i % 7 === 0);
      var daysAgo = (i * 3) % 420;
      var d = new Date(Date.now() - daysAgo * 86400000);
      var dateStr = String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0') + '/' + String(d.getFullYear()).slice(2);
      return {
        rating: rating,
        stars: '★'.repeat(rating) + '☆'.repeat(5 - rating),
        text: text,
        author: names[i % names.length] + ', ' + cities[i % cities.length],
        hasImage: hasImage,
        imgId: 'rev-img-' + i,
        date: dateStr,
        dateVal: d.getTime()
      };
    });
  }

  function initReviews() {
    var root = document.querySelector('[data-sielle-reviews]');
    if (!root || root.__init) return;
    root.__init = true;

    var allReviews = generateAllReviews();
    var reviewsPerPage = 12;
    var state = { filterStar: null, page: 0, sortMode: 'rating', lightboxId: null };

    var placeholderImg = root.getAttribute('data-placeholder-review-img') || '';

    var els = {
      avg: root.querySelector('[data-rev-avg]'),
      total: root.querySelector('[data-rev-total]'),
      bars: root.querySelector('[data-rev-bars]'),
      filterNotice: root.querySelector('[data-rev-filter-notice]'),
      list: root.querySelector('[data-rev-list]'),
      sortSelect: root.querySelector('[data-rev-sort]'),
      pageDisplay: root.querySelector('[data-rev-page-display]'),
      prevBtn: root.querySelector('[data-rev-prev]'),
      nextBtn: root.querySelector('[data-rev-next]'),
      lightbox: root.querySelector('[data-rev-lightbox]'),
      lightboxImg: root.querySelector('[data-rev-lightbox-img]')
    };

    function render() {
      var total = allReviews.length;
      var sum = allReviews.reduce(function (a, r) { return a + r.rating; }, 0);
      var avg = (sum / total).toFixed(1);
      var counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      allReviews.forEach(function (r) { counts[r.rating]++; });

      if (els.avg) els.avg.textContent = avg;
      if (els.total) els.total.textContent = total + ' calificaciones';

      if (els.bars) {
        els.bars.innerHTML = '';
        [5, 4, 3, 2, 1].forEach(function (n) {
          var active = state.filterStar === n;
          var pct = Math.round((counts[n] / total) * 100);
          var row = document.createElement('button');
          row.type = 'button';
          row.className = 'sielle-rev-bar-row';
          row.innerHTML =
            '<span class="sielle-rev-bar-label" style="color:' + (active ? '#A56E90' : '#57574f') + ';font-weight:' + (active ? '700' : '400') + ';">' + n + '★</span>' +
            '<span class="sielle-rev-bar-track"><span class="sielle-rev-bar-fill" style="width:' + pct + '%;"></span></span>' +
            '<span class="sielle-rev-bar-count">' + counts[n] + ' (' + pct + '%)</span>';
          row.addEventListener('click', function () {
            state.filterStar = state.filterStar === n ? null : n;
            state.page = 0;
            render();
          });
          els.bars.appendChild(row);
        });
      }

      if (els.filterNotice) {
        if (state.filterStar) {
          els.filterNotice.style.display = '';
          els.filterNotice.textContent = 'Mostrando ' + state.filterStar + '★ · quitar filtro';
          els.filterNotice.onclick = function () { state.filterStar = null; state.page = 0; render(); };
        } else {
          els.filterNotice.style.display = 'none';
        }
      }

      var base = state.filterStar ? allReviews.filter(function (r) { return r.rating === state.filterStar; }) : allReviews.slice();
      var filtered = base.sort(function (a, b) {
        if (state.sortMode === 'images') return (b.hasImage - a.hasImage) || (b.rating - a.rating);
        if (state.sortMode === 'recent') return b.dateVal - a.dateVal;
        return b.rating - a.rating;
      });
      var totalPages = Math.max(1, Math.ceil(filtered.length / reviewsPerPage));
      var page = Math.min(state.page, totalPages - 1);
      var paged = filtered.slice(page * reviewsPerPage, page * reviewsPerPage + reviewsPerPage);

      if (els.list) {
        els.list.innerHTML = '';
        paged.forEach(function (rev) {
          var item = document.createElement('div');
          item.className = 'sielle-rev-item';
          var html =
            '<div class="sielle-rev-item-top"><div class="sielle-rev-stars-gold">' + rev.stars + '</div><span class="sielle-rev-date">' + rev.date + '</span></div>' +
            '<div class="sielle-rev-author-row"><span class="sielle-rev-author">' + rev.author + '</span><span class="sielle-rev-verified">Verificado</span></div>';
          if (rev.text) html += '<p class="sielle-rev-text">' + rev.text + '</p>';
          if (rev.hasImage) html += '<div class="sielle-rev-thumb" data-rev-thumb data-img-id="' + rev.imgId + '"><img src="' + placeholderImg + '" alt="Foto del cliente" loading="lazy"></div>';
          item.innerHTML = html;
          els.list.appendChild(item);
        });
        els.list.querySelectorAll('[data-rev-thumb]').forEach(function (thumb) {
          thumb.addEventListener('click', function () {
            if (els.lightbox && els.lightboxImg) {
              els.lightboxImg.src = thumb.querySelector('img').src;
              els.lightbox.classList.add('is-open');
            }
          });
        });
      }
      if (els.pageDisplay) els.pageDisplay.textContent = 'Página ' + (page + 1) + ' de ' + totalPages;
    }

    if (els.sortSelect) els.sortSelect.addEventListener('change', function (e) {
      state.sortMode = e.target.value; state.page = 0; render();
    });
    if (els.prevBtn) els.prevBtn.addEventListener('click', function () { state.page = Math.max(0, state.page - 1); render(); window.scrollTo({ top: root.offsetTop - 40, behavior: 'smooth' }); });
    if (els.nextBtn) els.nextBtn.addEventListener('click', function () { state.page = state.page + 1; render(); window.scrollTo({ top: root.offsetTop - 40, behavior: 'smooth' }); });
    if (els.lightbox) els.lightbox.addEventListener('click', function () { els.lightbox.classList.remove('is-open'); });

    render();
  }

  /* ---------------------------------------------------------------- */
  /* Boot                                                               */
  /* ---------------------------------------------------------------- */
  function boot() {
    wireAddToCart();
    initMarquee();
    initCounters();
    initStickyBar();
    initTracks();
    initHeroGallery();
    initReviews();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
